import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import facilities from "../../constants/facilities_detail.json";
import { FacilityDetailContainer } from "containers/FacilitiesContainer";
import { FUNERAL_SIDO, FUNERAL_SIGUNGU } from "goi_common";
import RichSearch from "@/components/common/RichSearch";
import { richSearchData } from "@/constants/richSearch";
import { getAddressRichSearch, getAggregateRating } from "@/utils/richSearch";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

type FacilitiesElement = typeof facilities[0];

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = facilities.map((facility) => ({
    params: {
      id: facility.id.toString(),
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string | undefined;

  const findedFacilities: FacilitiesElement | undefined = id
    ? facilities.find((facility) => facility.id === parseInt(id))
    : undefined;
  let district;
  let name = "";
  let description = "";
  let hallrentString = "";

  if (findedFacilities) {
    const originSigungu =
      FUNERAL_SIGUNGU.get(Number(findedFacilities.sido))?.get(Number(findedFacilities.sigungu)) ||
      "";
    const sigungu = originSigungu?.length > 2 ? originSigungu?.slice(0, -1) : originSigungu;

    name = findedFacilities.companyname.includes("납골당")
      ? findedFacilities.companyname
      : findedFacilities.companyname + " 납골당";
    district = sigungu;

    hallrentString = findedFacilities.hallrents
      .map((res) => `${res.item}: ${res.charge?.toLocaleString()}원`)
      .join(", ");

    description = ` ${name} 정보, 비용, 할인 안내, ${findedFacilities.fulladdress}, ${name} 비용 ${hallrentString}`;
  }

  return {
    props: {
      defaultFacility: findedFacilities,
      pageDescription: description,
      pageKeywords: `${name}, ${name} 가격, ${name} 위치, ${name} 후기, ${name} 정보, 장지검색, 봉안, 자연, 매장, 봉안당, 수목장, 화초장, 잔디장, 매장묘, 납골당, ${district} 납골당, ${district} 납골당, ${district} 수목장, 납골당 비용, 수목장 비용`,
      pageOgTitle: `${district} 납골당, 장지, ${name} 정보 (위치, 가격, 비용, 평점) - [우리가 찾던 장례전문가, 고이]`,
      pageOgDescription: description,
      pageOgImage:
        findedFacilities?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
    },
  };
};

export const excludeFacilities = ["5", "200", "174", "702", "832", "1028"];

function FacilityId({
  defaultFacility,
  pageOgTitle,
}: {
  defaultFacility: FacilitiesElement;
  pageOgTitle: string;
}) {
  const { defaultAggregateRatingData, FAQRichSearch } = richSearchData.facilities;
  const { sido, sigungu, companyname } = defaultFacility;

  const aggregateRatingRichSearchName = companyname || "고이";

  // aggregate rich search
  const aggregateRatingRichSearch = getAggregateRating("Product", {
    name: aggregateRatingRichSearchName,
    aggregateRating: defaultAggregateRatingData,
  });

  // address rich search
  const sidoNumber = Number(sido);
  const sigunguNumber = Number(sigungu);

  const sidoKR = FUNERAL_SIDO.get(sidoNumber);
  const signuguKR = FUNERAL_SIGUNGU.get(sidoNumber)?.get(sigunguNumber);

  const addressRichSearch = getAddressRichSearch(companyname, {
    streetAddress: defaultFacility?.fulladdress,
    addressRegion: sidoKR,
    addressLocality: signuguKR,
    postalCode: "",
  });

  const isExcluded = excludeFacilities.includes(String(defaultFacility.id));
  return (
    <>
      <RichSearch data={addressRichSearch} />
      <RichSearch data={aggregateRatingRichSearch} />
      <RichSearch data={FAQRichSearch} />

      <FacilityDetailContainer facility={defaultFacility} isExcluded={isExcluded} />
    </>
  );
}

export default FacilityId;
